import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Save,
  Trash2,
  User,
  MessageSquare,
  MapPin,
  FileText,
  ChevronDown,
  ChevronUp,
  Loader2,
  X,
  Image,
  ChevronLeft,
  ChevronRight,
  Calendar,
  ExternalLink,
  ShieldCheck,
  ShieldOff,
  AlertTriangle,
  Phone,
  Navigation,
  Receipt,
  Plus,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import { Customer } from '../../types/customer'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import { SendSMSModal } from '../../components/SendSMSModal'
import { CommentSection } from '../../components/comments'
import { ActivityLog } from '../../components/ActivityLog'
import { logCustomerChange } from '../../services/activityLog'
import { usePriceListsByType, useCustomerRutApplicants, useCreateRutApplicant, useDeleteRutApplicant, useMoveRutApplicantUp, useMoveRutApplicantDown, queryKeys } from '../../hooks/useSupabase'
import { maskPersonalNumber, isValidPersonalNumber } from '../../services/rutApplicants'
import { AddressSearch } from '../../components/AddressSearch'

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  useAuth()
  const isNewCustomer = id === 'new'
  const [loading, setLoading] = useState(!isNewCustomer)
  const [saving, setSaving] = useState(false)
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [showSMSModal, setShowSMSModal] = useState(false)
  const [showUnprotectModal, setShowUnprotectModal] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)

  // Navigation between filtered customers
  const [filteredCustomerIds, setFilteredCustomerIds] = useState<string[]>([])

  // Load filtered customer IDs from sessionStorage
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('filtered-customer-ids')
      if (saved) {
        setFilteredCustomerIds(JSON.parse(saved))
      }
    } catch (e) {
      console.error('Error loading filtered customer IDs:', e)
    }
  }, [])

  // Calculate navigation state (with wrap-around)
  const currentIndex = id && !isNewCustomer ? filteredCustomerIds.indexOf(id) : -1
  const canNavigate = filteredCustomerIds.length > 1 && currentIndex >= 0
  const previousIndex = currentIndex > 0 ? currentIndex - 1 : filteredCustomerIds.length - 1
  const nextIndex = currentIndex < filteredCustomerIds.length - 1 ? currentIndex + 1 : 0

  // Reset lastSaved when navigating to a different customer
  useEffect(() => {
    setLastSaved(null)
  }, [id])

  // Navigate to previous customer (with wrap-around)
  const navigateToPrevious = () => {
    if (!canNavigate) return
    if (hasUnsavedChanges()) {
      if (!window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
        return
      }
    }
    navigate(`/admin/customers/${filteredCustomerIds[previousIndex]}`)
  }

  // Navigate to next customer (with wrap-around)
  const navigateToNext = () => {
    if (!canNavigate) return
    if (hasUnsavedChanges()) {
      if (!window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
        return
      }
    }
    navigate(`/admin/customers/${filteredCustomerIds[nextIndex]}`)
  }

  // Keyboard navigation with Page Up/Down
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) {
        return
      }
      if (e.key === 'PageUp') {
        e.preventDefault()
        navigateToPrevious()
      } else if (e.key === 'PageDown') {
        e.preventDefault()
        navigateToNext()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [canNavigate, filteredCustomerIds, previousIndex, nextIndex])

  // Collapsible section states
  const [basicInfoCollapsed, setBasicInfoCollapsed] = useState(false)
  const [imagesCollapsed, setImagesCollapsed] = useState(true)
  const [expandedImageAssignments, setExpandedImageAssignments] = useState<Set<string>>(new Set())
  const [activityLogCollapsed, setActivityLogCollapsed] = useState(true)

  // Report images from completed assignments
  interface ReportImage {
    url: string
    name: string
    uploaded_at: string
    type: 'before' | 'after' | 'other'
    article_name?: string
  }

  interface AssignmentWithImages {
    id: string
    assignment_number: string | null
    title: string
    start_date: string
    images: ReportImage[]
  }

  const [reportImagesByAssignment, setReportImagesByAssignment] = useState<AssignmentWithImages[]>([])
  const [loadingReportImages, setLoadingReportImages] = useState(false)

  // Track original data for unsaved changes detection
  const [originalFormData, setOriginalFormData] = useState<typeof formData | null>(null)

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    addressStreet: '',
    addressCity: '',
    addressPostalCode: '',
    addressCountry: 'Sverige',
    notes: '',
    active: true,
    priceListId: null as string | null,
    provisionPartnerId: null as string | null,
    hideDetailsFromTg: true,
    fortnoxCustomerId: '',
    wantsRut: false
  })

  // RUT applicants state
  const [showRutForm, setShowRutForm] = useState(false)
  const [rutFormData, setRutFormData] = useState({ name: '', personalNumber: '' })
  const [visiblePersonalNumbers, setVisiblePersonalNumbers] = useState<Set<string>>(new Set())

  // Fetch customer price lists
  const { data: customerPriceLists = [] } = usePriceListsByType('customer')

  // Fetch provision partners
  const [provisionPartners, setProvisionPartners] = useState<{ id: string; name: string; percentage: number }[]>([])
  useEffect(() => {
    const loadProvisionPartners = async () => {
      const { data, error } = await supabase
        .from('provision_partners')
        .select('id, name, percentage')
        .eq('is_active', true)
        .order('name')
      if (!error && data) {
        setProvisionPartners(data)
      }
    }
    loadProvisionPartners()
  }, [])

  // RUT applicants hooks
  const { data: rutApplicants = [] } = useCustomerRutApplicants(id || '')
  const createRutApplicant = useCreateRutApplicant()
  const deleteRutApplicant = useDeleteRutApplicant()
  const moveRutApplicantUp = useMoveRutApplicantUp()
  const moveRutApplicantDown = useMoveRutApplicantDown()

  // Toggle personal number visibility
  const togglePersonalNumberVisibility = (applicantId: string) => {
    setVisiblePersonalNumbers(prev => {
      const newSet = new Set(prev)
      if (newSet.has(applicantId)) {
        newSet.delete(applicantId)
      } else {
        newSet.add(applicantId)
      }
      return newSet
    })
  }

  // Handle adding RUT applicant
  const handleAddRutApplicant = async () => {
    if (!id || !rutFormData.name || !rutFormData.personalNumber) {
      toast.error('Ange både namn och personnummer')
      return
    }

    if (!isValidPersonalNumber(rutFormData.personalNumber)) {
      toast.error('Ogiltigt personnummer. Använd format YYYYMMDD-XXXX')
      return
    }

    try {
      await createRutApplicant.mutateAsync({
        customer_id: id,
        name: rutFormData.name,
        personal_number: rutFormData.personalNumber
      })
      toast.success('RUT-sökande tillagd')
      setShowRutForm(false)
      setRutFormData({ name: '', personalNumber: '' })
    } catch (error) {
      console.error('Error creating RUT applicant:', error)
      toast.error('Kunde inte lägga till RUT-sökande')
    }
  }

  // Handle deleting RUT applicant
  const handleDeleteRutApplicant = async (applicantId: string) => {
    if (!id) return

    try {
      await deleteRutApplicant.mutateAsync({ id: applicantId, customerId: id })
      toast.success('RUT-sökande borttagen')
    } catch (error) {
      console.error('Error deleting RUT applicant:', error)
      toast.error('Kunde inte ta bort RUT-sökande')
    }
  }

  // Set default price list for new customers
  useEffect(() => {
    if (isNewCustomer && customerPriceLists.length > 0 && !formData.priceListId) {
      const defaultPriceList = customerPriceLists.find(pl => pl.is_default)
      if (defaultPriceList) {
        setFormData(prev => ({
          ...prev,
          priceListId: defaultPriceList.id
        }))
      }
    }
  }, [isNewCustomer, customerPriceLists])

  // Check for unsaved changes
  const hasUnsavedChanges = (): boolean => {
    if (!originalFormData) return false
    return JSON.stringify(formData) !== JSON.stringify(originalFormData)
  }

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  })

  // Handle back/cancel button with unsaved changes confirmation
  const handleBack = () => {
    if (hasUnsavedChanges()) {
      if (window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
        navigate('/admin/customers')
      }
    } else {
      navigate('/admin/customers')
    }
  }

  // Load customer data (only if not new customer)
  useEffect(() => {
    if (!id) {
      navigate('/admin/customers')
      return
    }

    // If it's a new customer, don't load anything
    if (isNewCustomer) {
      setLoading(false)
      return
    }

    const loadCustomer = async () => {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('customers')
          .select('*')
          .eq('id', id)
          .single()

        if (error) {
          console.error('Error loading customer:', error)
          toast.error('Kunde inte ladda kund: ' + error.message)
          navigate('/admin/customers')
          return
        }

        if (!data) {
          toast.error('Kund hittades inte')
          navigate('/admin/customers')
          return
        }

        // Transform data to match our Customer interface
        const transformedCustomer: Customer = {
          id: data.id,
          fullName: data.full_name,
          email: data.email,
          phone: data.phone || '',
          addressStreet: data.address_street,
          addressCity: data.address_city,
          addressPostalCode: data.address_postal_code,
          addressCountry: data.address_country,
          notes: data.notes || undefined,
          active: data.active,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        }

        setCustomer(transformedCustomer)
        const loadedFormData = {
          fullName: transformedCustomer.fullName,
          email: transformedCustomer.email,
          phone: transformedCustomer.phone,
          addressStreet: transformedCustomer.addressStreet,
          addressCity: transformedCustomer.addressCity,
          addressPostalCode: transformedCustomer.addressPostalCode,
          addressCountry: transformedCustomer.addressCountry,
          notes: transformedCustomer.notes || '',
          active: transformedCustomer.active,
          priceListId: data.price_list_id || null,
          provisionPartnerId: data.provision_partner_id || null,
          hideDetailsFromTg: data.hide_details_from_tg || false,
          fortnoxCustomerId: data.fortnox_customer_id || '',
          wantsRut: data.wants_rut || false
        }
        setFormData(loadedFormData)
        setOriginalFormData(loadedFormData)
      } catch (error) {
        console.error('Error loading customer:', error)
        toast.error('Kunde inte ladda kund')
        navigate('/admin/customers')
      } finally {
        setLoading(false)
      }
    }

    loadCustomer()
  }, [id, navigate, isNewCustomer])

  // Get assignment count for this customer
  const { data: assignmentCount = 0 } = useQuery<number>({
    queryKey: ['customer-assignment-count', id],
    queryFn: async () => {
      if (!id || isNewCustomer) return 0

      const { count, error } = await supabase
        .from('assignments')
        .select('*', { count: 'exact', head: true })
        .eq('customer_id', id)

      if (error) {
        console.error('Error fetching assignment count:', error)
        return 0
      }

      return count ?? 0
    },
    enabled: !isNewCustomer && !!id
  })

  // Get SMS counts for this customer (total and unread)
  const { data: smsCounts = { total: 0, unread: 0 } } = useQuery<{ total: number; unread: number }>({
    queryKey: ['customer-sms-counts', id],
    queryFn: async () => {
      if (!id || isNewCustomer) return { total: 0, unread: 0 }

      // Get total SMS count
      const { count: totalCount, error: totalError } = await supabase
        .from('communications')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_type', 'customer')
        .eq('recipient_id', id)

      if (totalError) {
        console.error('Error fetching total SMS count:', totalError)
        return { total: 0, unread: 0 }
      }

      // Get unread inbound SMS count
      const { count: unreadCount, error: unreadError } = await supabase
        .from('communications')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_type', 'customer')
        .eq('recipient_id', id)
        .eq('direction', 'inbound')
        .is('read_at', null)

      if (unreadError) {
        console.error('Error fetching unread SMS count:', unreadError)
        return { total: totalCount ?? 0, unread: 0 }
      }

      return { total: totalCount ?? 0, unread: unreadCount ?? 0 }
    },
    enabled: !isNewCustomer && !!id
  })

  // Load report images from completed assignments
  useEffect(() => {
    if (isNewCustomer || !id) return

    const loadReportImages = async () => {
      setLoadingReportImages(true)
      try {
        // Fetch completed assignments with their report data
        const { data: assignments, error } = await supabase
          .from('assignments')
          .select(`
            id,
            assignment_number,
            title,
            start_date,
            assignment_subcontractors(
              reported_articles,
              report_images
            )
          `)
          .eq('customer_id', id)
          .in('status', ['pagaende', 'granskning', 'attesterad', 'slutford'])
          .order('start_date', { ascending: false })

        if (error) {
          console.error('Error loading report images:', error)
          return
        }

        // Process and group images by assignment
        const assignmentsWithImages: AssignmentWithImages[] = []

        for (const assignment of assignments || []) {
          const images: ReportImage[] = []

          // Collect images from all subcontractor reports
          for (const subcontractorReport of assignment.assignment_subcontractors || []) {
            // Add general report images
            const generalImages = subcontractorReport.report_images as ReportImage[] | null
            if (generalImages && Array.isArray(generalImages)) {
              images.push(...generalImages)
            }

            // Add article images
            const reportedArticles = subcontractorReport.reported_articles as Array<{
              article_id: string
              article_name: string
              images?: ReportImage[]
            }> | null
            if (reportedArticles && Array.isArray(reportedArticles)) {
              for (const article of reportedArticles) {
                if (article.images && Array.isArray(article.images)) {
                  images.push(...article.images.map(img => ({
                    ...img,
                    article_name: article.article_name
                  })))
                }
              }
            }
          }

          // Only include assignments that have images
          if (images.length > 0) {
            assignmentsWithImages.push({
              id: assignment.id,
              assignment_number: assignment.assignment_number,
              title: assignment.title,
              start_date: assignment.start_date,
              images
            })
          }
        }

        setReportImagesByAssignment(assignmentsWithImages)
      } catch (error) {
        console.error('Error loading report images:', error)
      } finally {
        setLoadingReportImages(false)
      }
    }

    loadReportImages()
  }, [isNewCustomer, id])


  const handleSave = async () => {
    if (!formData.fullName || !formData.email || !formData.phone) {
      toast.error('Namn, e-post och telefon är obligatoriska')
      return
    }

    try {
      setSaving(true)

      if (isNewCustomer) {
        // Create new customer
        const { data, error } = await supabase
          .from('customers')
          .insert({
            full_name: formData.fullName,
            email: formData.email,
            phone: formData.phone || null,
            address_street: formData.addressStreet,
            address_city: formData.addressCity,
            address_postal_code: formData.addressPostalCode,
            address_country: formData.addressCountry,
            notes: formData.notes || null,
            active: formData.active,
            price_list_id: formData.priceListId || null,
            provision_partner_id: formData.provisionPartnerId || null,
            hide_details_from_tg: formData.hideDetailsFromTg,
            fortnox_customer_id: formData.fortnoxCustomerId || null,
            wants_rut: formData.wantsRut
          })
          .select()
          .single()

        if (error) {
          console.error('Error creating customer:', error)
          toast.error('Kunde inte skapa kund: ' + error.message)
          return
        }

        // Log creation
        if (data?.id) {
          await logCustomerChange(data.id, 'created', {
            description: `Skapade kund: ${formData.fullName}`
          })
        }

        toast.success('Kund skapad framgångsrikt')
        // Navigate to the new customer's detail page
        if (data?.id) {
          navigate(`/admin/customers/${data.id}`)
        }
      } else {
        // Update existing customer
        if (!id) return

        // Detect what changed before updating
        const changedFields: { field: string; fieldName: string; oldValue: string; newValue: string }[] = []
        if (originalFormData) {
          if (formData.fullName !== originalFormData.fullName) {
            changedFields.push({ field: 'name', fieldName: 'namn', oldValue: originalFormData.fullName, newValue: formData.fullName })
          }
          if (formData.email !== originalFormData.email) {
            changedFields.push({ field: 'email', fieldName: 'e-post', oldValue: originalFormData.email, newValue: formData.email })
          }
          if (formData.phone !== originalFormData.phone) {
            changedFields.push({ field: 'phone', fieldName: 'telefon', oldValue: originalFormData.phone, newValue: formData.phone })
          }
          if (formData.addressStreet !== originalFormData.addressStreet) {
            changedFields.push({ field: 'address', fieldName: 'adress', oldValue: originalFormData.addressStreet, newValue: formData.addressStreet })
          }
          if (formData.addressCity !== originalFormData.addressCity) {
            changedFields.push({ field: 'city', fieldName: 'stad', oldValue: originalFormData.addressCity, newValue: formData.addressCity })
          }
          if (formData.addressPostalCode !== originalFormData.addressPostalCode) {
            changedFields.push({ field: 'postal_code', fieldName: 'postnummer', oldValue: originalFormData.addressPostalCode, newValue: formData.addressPostalCode })
          }
          if (formData.notes !== originalFormData.notes) {
            changedFields.push({ field: 'notes', fieldName: 'anteckningar', oldValue: originalFormData.notes, newValue: formData.notes })
          }
          if (formData.active !== originalFormData.active) {
            changedFields.push({ field: 'is_active', fieldName: 'aktivstatus', oldValue: originalFormData.active ? 'Aktiv' : 'Inaktiv', newValue: formData.active ? 'Aktiv' : 'Inaktiv' })
          }
          if (formData.priceListId !== originalFormData.priceListId) {
            const oldPriceList = customerPriceLists.find(pl => pl.id === originalFormData.priceListId)
            const newPriceList = customerPriceLists.find(pl => pl.id === formData.priceListId)
            changedFields.push({
              field: 'price_list_id',
              fieldName: 'prislista',
              oldValue: oldPriceList?.name || 'Standard',
              newValue: newPriceList?.name || 'Standard'
            })
          }
          if (formData.provisionPartnerId !== originalFormData.provisionPartnerId) {
            const oldPartner = provisionPartners.find(p => p.id === originalFormData.provisionPartnerId)
            const newPartner = provisionPartners.find(p => p.id === formData.provisionPartnerId)
            changedFields.push({
              field: 'provision_partner_id',
              fieldName: 'provisionspartner',
              oldValue: oldPartner?.name || 'Ingen',
              newValue: newPartner?.name || 'Ingen'
            })
          }
          if (formData.fortnoxCustomerId !== originalFormData.fortnoxCustomerId) {
            changedFields.push({ field: 'fortnox_customer_id', fieldName: 'Fortnox kund-ID', oldValue: originalFormData.fortnoxCustomerId, newValue: formData.fortnoxCustomerId })
          }
          if (formData.wantsRut !== originalFormData.wantsRut) {
            changedFields.push({ field: 'wants_rut', fieldName: 'RUT-avdrag', oldValue: originalFormData.wantsRut ? 'Ja' : 'Nej', newValue: formData.wantsRut ? 'Ja' : 'Nej' })
          }
        }

        const { error } = await supabase
          .from('customers')
          .update({
            full_name: formData.fullName,
            email: formData.email,
            phone: formData.phone || null,
            address_street: formData.addressStreet,
            address_city: formData.addressCity,
            address_postal_code: formData.addressPostalCode,
            address_country: formData.addressCountry,
            notes: formData.notes || null,
            active: formData.active,
            price_list_id: formData.priceListId || null,
            provision_partner_id: formData.provisionPartnerId || null,
            hide_details_from_tg: formData.hideDetailsFromTg,
            fortnox_customer_id: formData.fortnoxCustomerId || null,
            wants_rut: formData.wantsRut,
            updated_at: new Date().toISOString()
          })
          .eq('id', id)

        if (error) {
          console.error('Error updating customer:', error)
          toast.error('Kunde inte uppdatera kund: ' + error.message)
          return
        }

        // Log each change
        for (const change of changedFields) {
          await logCustomerChange(id, 'field_changed', {
            fieldName: change.field,
            oldValue: change.oldValue,
            newValue: change.newValue,
            description: `Ändrade ${change.fieldName}`
          })
        }

        // Refresh activity log if changes were logged
        if (changedFields.length > 0) {
          await queryClient.invalidateQueries({ queryKey: ['activity-log', 'customer', id] })
        }

        // Refresh price lists if price_list_id changed (for usage count)
        if (changedFields.some(f => f.field === 'price_list_id')) {
          await queryClient.invalidateQueries({ queryKey: queryKeys.priceLists })
        }

        toast.success('Kund uppdaterad framgångsrikt')
        setLastSaved(new Date())
        setOriginalFormData({ ...formData })
        // Reload customer data
        const { data } = await supabase
          .from('customers')
          .select('*')
          .eq('id', id)
          .single()

        if (data) {
          const transformedCustomer: Customer = {
            id: data.id,
            fullName: data.full_name,
            email: data.email,
            phone: data.phone || '',
            addressStreet: data.address_street,
            addressCity: data.address_city,
            addressPostalCode: data.address_postal_code,
            addressCountry: data.address_country,
            notes: data.notes || undefined,
            active: data.active,
            createdAt: data.created_at,
            updatedAt: data.updated_at
          }
          setCustomer(transformedCustomer)
        }
      }
    } catch (error) {
      console.error('Error saving customer:', error)
      toast.error('Kunde inte spara kund')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (isNewCustomer || !id) return
    if (!confirm('Är du säker på att du vill ta bort denna kund?')) {
      return
    }

    try {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', id)

      if (error) {
        console.error('Error deleting customer:', error)
        toast.error('Kunde inte ta bort kund: ' + error.message)
        return
      }

      toast.success('Kund borttagen')
      navigate('/admin/customers')
    } catch (error) {
      console.error('Error deleting customer:', error)
      toast.error('Kunde inte ta bort kund')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nature-600 mx-auto"></div>
          <p className="mt-4 text-nature-600 dark:text-nature-400">Laddar kund...</p>
        </div>
      </div>
    )
  }

  // Only return null if it's not a new customer and we don't have customer data
  if (!isNewCustomer && !customer) {
    return null
  }

  return (
    <div className="space-y-6">
      {/* Header Ribbon */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {!isNewCustomer && id && (
                <span className="text-sm font-mono text-nature-500 dark:text-nature-400">
                  #{id.substring(0, 8)}
                </span>
              )}
              <h1 className="text-lg sm:text-xl font-bold text-nature-900 dark:text-nature-100 truncate">
                {isNewCustomer ? 'Ny kund' : (formData.fullName || 'Namnlös kund')}
              </h1>
              {!isNewCustomer && (
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                  formData.active
                    ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200'
                    : 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200'
                }`}>
                  {formData.active ? 'Aktiv' : 'Inaktiv'}
                </span>
              )}
            </div>
            {isNewCustomer && (
              <p className="text-sm text-nature-500 dark:text-nature-400 mt-0.5">
                Fyll i uppgifterna nedan
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-2">
            {/* Navigation between filtered customers (with wrap-around) */}
            {!isNewCustomer && filteredCustomerIds.length > 1 && (
              <div className="flex items-center gap-1 border-r border-nature-200 dark:border-nature-600 pr-2 mr-1">
                <button
                  onClick={navigateToPrevious}
                  className="p-1.5 rounded-md transition-colors text-nature-600 dark:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-700"
                  title="Föregående kund (Page Up)"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="text-xs text-nature-500 dark:text-nature-400 min-w-[3rem] text-center">
                  {currentIndex + 1} / {filteredCustomerIds.length}
                </span>
                <button
                  onClick={navigateToNext}
                  className="p-1.5 rounded-md transition-colors text-nature-600 dark:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-700"
                  title="Nästa kund (Page Down)"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}

            {lastSaved && !hasUnsavedChanges() && (
              <span className="text-xs text-nature-500 dark:text-nature-400">
                Sparad {format(lastSaved, 'HH:mm')}
              </span>
            )}

            {/* Action buttons - only for existing customers */}
            {!isNewCustomer && (
              <div className="flex items-center gap-1">
                <button
                  onClick={assignmentCount === 0 ? handleDelete : undefined}
                  disabled={assignmentCount > 0}
                  title={assignmentCount > 0
                    ? `Kan inte ta bort - kunden har ${assignmentCount} kopplad${assignmentCount === 1 ? 't' : 'e'} uppdrag`
                    : 'Ta bort kund'
                  }
                  className={`p-2 rounded-lg transition-colors ${
                    assignmentCount > 0
                      ? 'text-nature-400 dark:text-nature-500 cursor-not-allowed'
                      : 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                  }`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )}

            <button
              onClick={handleBack}
              className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors w-full sm:w-auto ${
                hasUnsavedChanges()
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50 border border-amber-300 dark:border-amber-700'
                  : 'btn-outline'
              }`}
            >
              {hasUnsavedChanges() ? (
                <>
                  <X className="h-4 w-4" />
                  <span>Avbryt</span>
                </>
              ) : (
                <>
                  <ArrowLeft className="h-4 w-4" />
                  <span>Tillbaka</span>
                </>
              )}
            </button>

            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>{saving ? 'Sparar...' : 'Spara'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 1. Grundinformation - Expanded by default */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
        <button
          onClick={() => setBasicInfoCollapsed(!basicInfoCollapsed)}
          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors rounded-t-lg"
        >
          <div className="flex items-center space-x-2">
            <User className="h-5 w-5 text-nature-600 dark:text-nature-400" />
            <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">Grundinformation</h3>
          </div>
          {basicInfoCollapsed ? (
            <ChevronDown className="h-5 w-5 text-nature-500" />
          ) : (
            <ChevronUp className="h-5 w-5 text-nature-500" />
          )}
        </button>
        {!basicInfoCollapsed && (
          <div className="px-6 pb-6 space-y-4 border-t border-nature-200 dark:border-nature-700 pt-4">
            {/* Contact info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Förnamn och efternamn *</label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                  className="input-field"
                  placeholder="Anna Andersson"
                />
              </div>
              <div>
                <label className="form-label">E-post *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="input-field"
                  placeholder="anna@email.com"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Telefon *</label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="input-field flex-1"
                    placeholder="+46 70 123 45 67"
                  />
                  {formData.phone && (
                    <a
                      href={`tel:${formData.phone.replace(/\s+/g, '')}`}
                      className="flex items-center justify-center px-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                      title="Ring kund"
                    >
                      <Phone className="h-5 w-5" />
                    </a>
                  )}
                </div>
              </div>
              <div>
                <label className="form-label flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Prislista
                </label>
                <select
                  value={formData.priceListId || ''}
                  onChange={(e) => setFormData({ ...formData, priceListId: e.target.value || null })}
                  className="input-field"
                >
                  {customerPriceLists.map((priceList) => (
                    <option key={priceList.id} value={priceList.id}>
                      {priceList.name}
                      {priceList.is_default ? ' (Standard)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Fortnox kund-ID</label>
                <input
                  type="text"
                  value={formData.fortnoxCustomerId}
                  onChange={(e) => setFormData({...formData, fortnoxCustomerId: e.target.value})}
                  className="input-field"
                  placeholder="Ange Fortnox kund-ID"
                />
              </div>
              <div>
                <label className="form-label">Provisionspartner</label>
                <select
                  value={formData.provisionPartnerId || ''}
                  onChange={(e) => setFormData({ ...formData, provisionPartnerId: e.target.value || null })}
                  className="input-field"
                >
                  <option value="">Ingen provisionspartner</option>
                  {provisionPartners.map((partner) => (
                    <option key={partner.id} value={partner.id}>
                      {partner.name} ({partner.percentage}%)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Address */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
              <div className="md:col-span-3 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="form-label flex items-center gap-2 mb-0">
                    <MapPin className="h-4 w-4" />
                    Adress
                  </label>
                  {(formData.addressStreet || formData.addressCity) && (
                    <button
                      type="button"
                      onClick={() => {
                        const parts = [formData.addressStreet, formData.addressPostalCode, formData.addressCity, formData.addressCountry].filter(Boolean)
                        if (parts.length > 0) {
                          window.open(`https://www.google.com/maps?q=${encodeURIComponent(parts.join(', '))}`, '_blank')
                        }
                      }}
                      className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                      title="Öppna i Google Maps"
                    >
                      <Navigation className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <AddressSearch
                  onSelect={(address) => {
                    setFormData({
                      ...formData,
                      addressStreet: address.street,
                      addressPostalCode: address.postalCode,
                      addressCity: address.city,
                      addressCountry: address.country
                    })
                  }}
                />
                <input
                  type="text"
                  value={formData.addressStreet}
                  onChange={(e) => setFormData({...formData, addressStreet: e.target.value})}
                  className="input-field"
                  placeholder="Gatuadress"
                />
              </div>
              <div>
                <label className="form-label">Postnummer</label>
                <input
                  type="text"
                  value={formData.addressPostalCode}
                  onChange={(e) => setFormData({...formData, addressPostalCode: e.target.value})}
                  className="input-field"
                  placeholder="123 45"
                />
              </div>
              <div>
                <label className="form-label">Ort</label>
                <input
                  type="text"
                  value={formData.addressCity}
                  onChange={(e) => setFormData({...formData, addressCity: e.target.value})}
                  className="input-field"
                  placeholder="Stockholm"
                />
              </div>
              <div>
                <label className="form-label">Land</label>
                <input
                  type="text"
                  value={formData.addressCountry}
                  onChange={(e) => setFormData({...formData, addressCountry: e.target.value})}
                  className="input-field"
                  placeholder="Sverige"
                />
              </div>
            </div>

            {/* Two-column section: Notes on left, Status/links on right */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-nature-100 dark:border-nature-700">
              {/* Left column - Notes */}
              <div>
                <label className="form-label flex items-center space-x-2">
                  <FileText className="h-4 w-4" />
                  <span>Anteckningar</span>
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="input-field"
                  rows={8}
                  placeholder="Lägg till anteckningar om kunden..."
                />
              </div>

              {/* Right column - Status and links */}
              <div className="space-y-4">
                {/* Status toggles */}
                <div className="bg-nature-50 dark:bg-nature-900/50 rounded-lg p-4 space-y-3">
                  <label className="form-label text-xs uppercase tracking-wider text-nature-500">Status</label>
                  <div className="flex flex-wrap gap-2">
                    {/* Active/Inactive toggle */}
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, active: !formData.active })}
                      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        formData.active
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 ring-1 ring-green-300 dark:ring-green-700'
                          : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 ring-1 ring-red-300 dark:ring-red-700'
                      }`}
                    >
                      <div className={`w-2 h-2 rounded-full ${formData.active ? 'bg-green-500' : 'bg-red-500'}`} />
                      {formData.active ? 'Aktiv' : 'Inaktiv'}
                    </button>

                    {/* Privacy toggle - hide details from TG */}
                    <button
                      type="button"
                      onClick={() => {
                        if (formData.hideDetailsFromTg) {
                          // Warn before unprotecting
                          setShowUnprotectModal(true)
                        } else {
                          // Protect without warning
                          setFormData({ ...formData, hideDetailsFromTg: true })
                        }
                      }}
                      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        formData.hideDetailsFromTg
                          ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 ring-1 ring-amber-300 dark:ring-amber-700'
                          : 'bg-nature-100 dark:bg-nature-800 text-nature-600 dark:text-nature-400 ring-1 ring-nature-200 dark:ring-nature-700'
                      }`}
                      title="TG ser endast adress, inte namn eller telefonnummer"
                    >
                      {formData.hideDetailsFromTg ? (
                        <ShieldCheck className="h-4 w-4 text-amber-600" />
                      ) : (
                        <ShieldOff className="h-4 w-4 text-nature-400" />
                      )}
                      {formData.hideDetailsFromTg ? 'Skyddad' : 'Ej skyddad'}
                    </button>
                  </div>
                </div>

                {/* RUT-avdrag sektion */}
                <div className="bg-nature-50 dark:bg-nature-900/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="form-label text-xs uppercase tracking-wider text-nature-500 flex items-center gap-2 mb-0">
                      <Receipt className="h-3.5 w-3.5" />
                      RUT-avdrag
                    </label>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, wantsRut: !formData.wantsRut })}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        formData.wantsRut
                          ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 ring-1 ring-primary-300 dark:ring-primary-700'
                          : 'bg-nature-200 dark:bg-nature-700 text-nature-500 dark:text-nature-400'
                      }`}
                    >
                      {formData.wantsRut ? 'Ja' : 'Nej'}
                    </button>
                  </div>

                  {/* RUT-sökande lista - endast om kunden vill ha RUT */}
                  {formData.wantsRut && !isNewCustomer && (
                    <div className="mt-3 pt-3 border-t border-nature-200 dark:border-nature-700">
                      <label className="text-xs text-nature-500 dark:text-nature-400 mb-2 block">RUT-sökande</label>

                      {/* Lista med RUT-sökande */}
                      {rutApplicants.length > 0 && (
                        <div className="space-y-2 mb-3">
                          {rutApplicants.map((applicant, index) => {
                            const isVisible = visiblePersonalNumbers.has(applicant.id)
                            const isFirst = index === 0

                            return (
                              <div
                                key={applicant.id}
                                className={`flex items-center justify-between bg-white dark:bg-nature-800 rounded-lg px-3 py-2 ring-1 ${
                                  isFirst
                                    ? 'ring-primary-300 dark:ring-primary-700 border-l-4 border-primary-500'
                                    : 'ring-nature-200 dark:ring-nature-700'
                                }`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-nature-900 dark:text-nature-100 truncate">
                                      {applicant.name}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-xs text-nature-500 dark:text-nature-400 font-mono">
                                      {isVisible ? applicant.personal_number : maskPersonalNumber(applicant.personal_number)}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => togglePersonalNumberVisibility(applicant.id)}
                                      className="p-0.5 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 transition-colors"
                                      title={isVisible ? 'Dölj personnummer' : 'Visa personnummer'}
                                    >
                                      {isVisible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                                    </button>
                                  </div>
                                  <span className={`text-[10px] ${isFirst ? 'text-primary-600 dark:text-primary-400' : 'text-nature-400'}`}>
                                    {isFirst ? 'Huvudsökande' : 'Medsökande'}
                                  </span>
                                </div>

                                {/* Knappar för att flytta och ta bort */}
                                <div className="flex items-center gap-1 ml-2">
                                  {/* Flytta upp */}
                                  <button
                                    type="button"
                                    onClick={() => moveRutApplicantUp.mutate({ customerId: id!, applicantId: applicant.id })}
                                    disabled={index === 0 || moveRutApplicantUp.isPending}
                                    className="p-1 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    title="Flytta upp"
                                  >
                                    <ArrowUp className="h-4 w-4" />
                                  </button>
                                  {/* Flytta ner */}
                                  <button
                                    type="button"
                                    onClick={() => moveRutApplicantDown.mutate({ customerId: id!, applicantId: applicant.id })}
                                    disabled={index === rutApplicants.length - 1 || moveRutApplicantDown.isPending}
                                    className="p-1 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    title="Flytta ner"
                                  >
                                    <ArrowDown className="h-4 w-4" />
                                  </button>
                                  {/* Ta bort */}
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteRutApplicant(applicant.id)}
                                    disabled={deleteRutApplicant.isPending}
                                    className="p-1 text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                                    title="Ta bort"
                                  >
                                    {deleteRutApplicant.isPending ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}

                      {/* Formulär för att lägga till RUT-sökande */}
                      {showRutForm ? (
                        <div className="bg-white dark:bg-nature-800 rounded-lg p-3 ring-1 ring-nature-200 dark:ring-nature-700 space-y-3">
                          <div>
                            <label className="text-xs text-nature-500 dark:text-nature-400 mb-1 block">Namn</label>
                            <input
                              type="text"
                              value={rutFormData.name}
                              onChange={(e) => setRutFormData(prev => ({ ...prev, name: e.target.value }))}
                              className="input-field text-sm"
                              placeholder="Förnamn Efternamn"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-nature-500 dark:text-nature-400 mb-1 block">Personnummer</label>
                            <input
                              type="text"
                              value={rutFormData.personalNumber}
                              onChange={(e) => setRutFormData(prev => ({ ...prev, personalNumber: e.target.value }))}
                              className="input-field text-sm font-mono"
                              placeholder="YYYYMMDD-XXXX"
                            />
                          </div>
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setShowRutForm(false)
                                setRutFormData({ name: '', personalNumber: '' })
                              }}
                              className="px-3 py-1.5 text-sm text-nature-600 dark:text-nature-400 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
                            >
                              Avbryt
                            </button>
                            <button
                              type="button"
                              onClick={handleAddRutApplicant}
                              disabled={createRutApplicant.isPending}
                              className="px-3 py-1.5 text-sm bg-primary-500 text-white hover:bg-primary-600 rounded-lg transition-colors flex items-center gap-1"
                            >
                              {createRutApplicant.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Plus className="h-3.5 w-3.5" />
                              )}
                              Lägg till
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowRutForm(true)}
                          className="w-full py-2 text-sm text-nature-600 dark:text-nature-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-white dark:hover:bg-nature-800 rounded-lg border border-dashed border-nature-300 dark:border-nature-600 transition-colors flex items-center justify-center gap-1.5"
                        >
                          <Plus className="h-4 w-4" />
                          Lägg till sökande
                        </button>
                      )}
                    </div>
                  )}

                  {/* Visa meddelande om man behöver spara kunden först */}
                  {formData.wantsRut && isNewCustomer && (
                    <p className="text-xs text-nature-500 dark:text-nature-400 mt-2 italic">
                      Spara kunden först för att lägga till RUT-sökande.
                    </p>
                  )}
                </div>

                {/* Quick links - only show for existing customers */}
                {!isNewCustomer && customer && (
                  <div className="bg-nature-50 dark:bg-nature-900/50 rounded-lg p-4">
                    <label className="form-label text-xs uppercase tracking-wider text-nature-500 mb-3">Snabblänkar</label>
                    <div className="flex flex-col gap-2">
                      {/* Uppdrag - blue */}
                      <Link
                        to="/admin/assignments"
                        onClick={() => {
                          sessionStorage.setItem('assignmentFilters', JSON.stringify({
                            showAdvancedFilters: true,
                            dateFrom: '',
                            dateTo: '',
                            selectedStatuses: [],
                            selectedSubcontractors: [],
                            selectedCustomers: [customer.id],
                            selectedCities: []
                          }))
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-white dark:bg-nature-800 text-nature-700 dark:text-nature-200 hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors ring-1 ring-nature-200 dark:ring-nature-700"
                      >
                        <Calendar className="h-4 w-4 text-primary-500" />
                        Visa uppdrag
                        <span className="ml-auto flex items-center gap-2">
                          <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 text-xs font-medium rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
                            {assignmentCount}
                          </span>
                          <ChevronRight className="h-4 w-4 text-nature-400" />
                        </span>
                      </Link>
                      {/* SMS */}
                      <Link
                        to="/admin/communications"
                        onClick={() => {
                          sessionStorage.setItem('smsFilters', JSON.stringify({
                            recipientType: 'customer',
                            recipientId: customer.id,
                            recipientName: customer.fullName
                          }))
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-white dark:bg-nature-800 text-nature-700 dark:text-nature-200 hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors ring-1 ring-nature-200 dark:ring-nature-700"
                      >
                        <MessageSquare className="h-4 w-4 text-nature-500" />
                        Visa SMS
                        <span className="ml-auto flex items-center gap-2">
                          <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 text-xs font-medium rounded-full bg-nature-200 dark:bg-nature-700 text-nature-600 dark:text-nature-300">
                            {smsCounts.total}
                          </span>
                          {smsCounts.unread > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 text-xs font-bold rounded-full bg-red-500 text-white">
                              {smsCounts.unread}
                            </span>
                          )}
                          <ChevronRight className="h-4 w-4 text-nature-400" />
                        </span>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 6. Bilder - Collapsed by default, only for existing customers */}
      {!isNewCustomer && id && (
        <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
          <button
            onClick={() => setImagesCollapsed(!imagesCollapsed)}
            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors rounded-t-lg"
          >
            <div className="flex items-center space-x-2">
              <Image className="h-5 w-5 text-nature-600 dark:text-nature-400" />
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">Bilder</h3>
              {reportImagesByAssignment.length > 0 && (
                <span className="ml-1 px-2 py-0.5 text-xs font-medium rounded-full bg-nature-200 dark:bg-nature-600 text-nature-700 dark:text-nature-200">
                  {reportImagesByAssignment.reduce((total, a) => total + a.images.length, 0)}
                </span>
              )}
            </div>
            {imagesCollapsed ? (
              <ChevronDown className="h-5 w-5 text-nature-500" />
            ) : (
              <ChevronUp className="h-5 w-5 text-nature-500" />
            )}
          </button>
          {!imagesCollapsed && (
            <div className="border-t border-nature-200 dark:border-nature-700">
              {loadingReportImages ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-nature-500" />
                </div>
              ) : reportImagesByAssignment.length === 0 ? (
                <div className="text-center py-8 text-nature-500 dark:text-nature-400 p-6">
                  <Image className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Inga bilder ännu</p>
                  <p className="text-xs mt-1">Bilder från slutförda uppdrag visas här</p>
                </div>
              ) : (
                <div className="divide-y divide-nature-200 dark:divide-nature-700">
                  {reportImagesByAssignment.map((assignment) => {
                    // Group images by type
                    const beforeImages = assignment.images.filter(img => img.type === 'before')
                    const afterImages = assignment.images.filter(img => img.type === 'after')
                    const otherImages = assignment.images.filter(img => img.type === 'other')
                    const isExpanded = expandedImageAssignments.has(assignment.id)

                    const toggleExpanded = () => {
                      setExpandedImageAssignments(prev => {
                        const newSet = new Set(prev)
                        if (newSet.has(assignment.id)) {
                          newSet.delete(assignment.id)
                        } else {
                          newSet.add(assignment.id)
                        }
                        return newSet
                      })
                    }

                    return (
                      <div key={assignment.id} className="p-4">
                        {/* Assignment header - clickable to expand/collapse */}
                        <div className="flex items-center justify-between">
                          <button
                            onClick={toggleExpanded}
                            className="flex items-center gap-2 group flex-1 text-left"
                          >
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4 text-nature-400" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-nature-400" />
                            )}
                            <span className="text-sm font-semibold text-nature-900 dark:text-nature-100 group-hover:text-primary-600 dark:group-hover:text-primary-400">
                              {assignment.title || 'Namnlöst uppdrag'}
                            </span>
                            {assignment.assignment_number && (
                              <span className="text-xs text-nature-400 dark:text-nature-500 font-mono bg-nature-100 dark:bg-nature-700 px-1.5 py-0.5 rounded">
                                #{assignment.assignment_number}
                              </span>
                            )}
                            <span className="text-xs text-nature-500 dark:text-nature-400">
                              {format(new Date(assignment.start_date), 'yyyy-MM-dd', { locale: sv })}
                            </span>
                            <span className="text-xs text-nature-400 dark:text-nature-500">
                              ({assignment.images.length} bilder)
                            </span>
                          </button>
                          <Link
                            to={`/admin/assignments/${assignment.id}`}
                            className="p-1 hover:bg-nature-100 dark:hover:bg-nature-700 rounded"
                            title="Öppna uppdrag"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-4 w-4 text-nature-400 hover:text-primary-500" />
                          </Link>
                        </div>

                        {/* Images - only show when expanded */}
                        {isExpanded && (
                          <div className="mt-3 ml-6">
                        {/* Before images */}
                        {beforeImages.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-2">
                              Före ({beforeImages.length})
                            </p>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                              {beforeImages.map((img, idx) => (
                                <a
                                  key={idx}
                                  href={img.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="relative aspect-square rounded-lg overflow-hidden border-2 border-amber-300 dark:border-amber-600 hover:opacity-80 transition-opacity group"
                                >
                                  <img
                                    src={img.url}
                                    alt={img.name}
                                    className="w-full h-full object-cover"
                                  />
                                  {img.article_name && (
                                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs px-1 py-0.5 truncate">
                                      {img.article_name}
                                    </div>
                                  )}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* After images */}
                        {afterImages.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-2">
                              Efter ({afterImages.length})
                            </p>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                              {afterImages.map((img, idx) => (
                                <a
                                  key={idx}
                                  href={img.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="relative aspect-square rounded-lg overflow-hidden border-2 border-green-300 dark:border-green-600 hover:opacity-80 transition-opacity group"
                                >
                                  <img
                                    src={img.url}
                                    alt={img.name}
                                    className="w-full h-full object-cover"
                                  />
                                  {img.article_name && (
                                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs px-1 py-0.5 truncate">
                                      {img.article_name}
                                    </div>
                                  )}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Other images */}
                        {otherImages.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-nature-600 dark:text-nature-400 mb-2">
                              Övriga ({otherImages.length})
                            </p>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                              {otherImages.map((img, idx) => (
                                <a
                                  key={idx}
                                  href={img.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="relative aspect-square rounded-lg overflow-hidden border-2 border-nature-300 dark:border-nature-600 hover:opacity-80 transition-opacity group"
                                >
                                  <img
                                    src={img.url}
                                    alt={img.name}
                                    className="w-full h-full object-cover"
                                  />
                                  {img.article_name && (
                                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs px-1 py-0.5 truncate">
                                      {img.article_name}
                                    </div>
                                  )}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 7. Kommentarer - Collapsed by default, only for existing customers */}
      {!isNewCustomer && customer && (
        <CommentSection
          entityType="customer"
          entityId={customer.id}
          title="Kommentarer"
          defaultCollapsed={true}
        />
      )}


      {/* 9. Aktivitetslogg - Collapsed by default, only for existing customers */}
      {!isNewCustomer && id && (
        <ActivityLog
          customerId={id}
          entityType="customer"
          isCollapsed={activityLogCollapsed}
          onToggleCollapse={() => setActivityLogCollapsed(!activityLogCollapsed)}
        />
      )}

      {/* Send SMS Modal */}
      {customer && (
        <SendSMSModal
          isOpen={showSMSModal}
          onClose={() => setShowSMSModal(false)}
          recipientType="customer"
          recipientId={customer.id}
          recipientName={customer.fullName}
          recipientPhone={customer.phone}
        />
      )}

      {/* Unprotect Customer Confirmation Modal */}
      {showUnprotectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                Ta bort skydd?
              </h3>
            </div>

            <p className="text-sm text-nature-600 dark:text-nature-400 mb-6">
              Om du tar bort skyddet kommer trädgårdsmästare att kunna se kundens <strong>namn</strong> och <strong>telefonnummer</strong> på uppdragen.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowUnprotectModal(false)}
                className="px-4 py-2 text-sm font-medium text-nature-700 dark:text-nature-300 bg-nature-100 dark:bg-nature-700 hover:bg-nature-200 dark:hover:bg-nature-600 rounded-lg transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  setFormData({ ...formData, hideDetailsFromTg: false })
                  setShowUnprotectModal(false)
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <ShieldOff className="h-4 w-4" />
                Ta bort skydd
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

